import React, { useCallback, useState } from 'react';
import { TimeCardEntry } from './TimeCard.types.ts';
import {
  Alert,
  AlertText,
  ButtonAsync,
  Center,
  Divider,
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  Icons,
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  Text,
  Textarea,
  TextareaInput,
  useAlert,
  VStack,
  MessagesForEntity,
  useTimeEntry,
  useUser,
} from '@tonic/central-specialties-ui-themed';
import { DB, decimalHoursToTimeString } from '@tonic/central-specialties-utils';
import {
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Icon,
  ScrollView,
} from '@gluestack-ui/themed';
import moment from 'moment';
import { TimeCardEntrySummaryCard } from './TimeCardEntrySummaryCard.tsx';
import { TimeCardEntryForm } from '../general/Form/specific/TimeCardEntry/TimeCardEntryForm.tsx';
import { EntityCardColumn } from '../general/EntityCardExplorer/EntityCardColumnsView/EntityCardColumn/EntityCardColumn.tsx';
import { useMutation } from '@tanstack/react-query';
import { TimeCard } from '../../routes/root/TimeCardsPage/TimeCardsPage.tsx';

export interface EmployeeWorkdaySummaryModalProps {
  timeCard: TimeCard;
  date: Date;
  isOpen: boolean;
  onClose: () => void;
  openUnlockModal: (timeCard: TimeCard) => void;
  refetch?: () => void;
}

/**
 * Pops up the native browser confirm dialog and returns a Promise<boolean>
 */
function askProceedConfirmationWeb(): Promise<boolean> {
  return Promise.resolve(
    window.confirm(
      'There is a denied entry in this list. Do you still want to proceed and approve all time card entries?',
    ),
  );
}

export const EmployeeWorkdaySummaryModal = ({
  timeCard,
  date,
  isOpen,
  onClose,
  openUnlockModal,
  refetch,
}: EmployeeWorkdaySummaryModalProps) => {
  const { alert } = useAlert();
  const user = useUser();
  const [isPayrollAdmin] = useState(
    user?.permissions?.roles?.includes('PayrollAdmin') ?? false,
  );

  const [selectedTimeCardIndex, setSelectedTimeCardIndex] = useState<
    number | null
  >(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showDenyMode, setShowDenyMode] = useState(false);
  const { postTimeCardRejectionById, submitTimeCardMessage } = useTimeEntry();
  const [activeDenialReason, setActiveDenialReason] = useState<string>();
  const shouldShowDeleteButton =
    timeCard.status !== 'Approved' ||
    (timeCard.status === 'Approved' && isPayrollAdmin);
  const isDisabledStatus =
    selectedTimeCardIndex !== null && selectedTimeCardIndex >= 0
      ? !timeCard.timeCardEntries[selectedTimeCardIndex]
      : true;

  const {
    mutateAsync: submitApproveTimeCard,
    isPending: approvalRequestPending,
  } = useMutation({
    mutationFn: (timeCardEntryIds: string[]) =>
      DB.POST('/time-card-entries/approve', {
        body: {
          timeCardEntryIds,
        },
      }),
  });

  const approveTimeCard = useCallback(async () => {
    const denialCheck =
      timeCard?.timeCardEntries?.some((entry) => entry.status === 'Denied') ??
      false;

    if (denialCheck) {
      const proceed = await askProceedConfirmationWeb();

      if (!proceed) {
        console.log('User cancelled time approval due to denied entries.');
        return;
      }
    }

    try {
      const { error } = await submitApproveTimeCard(timeCard.timeCardEntryIds);
      if (error) {
        throw error;
      }
      alert({
        status: 'success',
        message: 'Time card approved',
        timeout: 5000,
      });
      refetch && refetch();
      onClose();
    } catch (error) {
      console.error(error);
      alert({
        status: 'error',
        message: 'There was an error approving this time card entry',
        timeout: 10000,
      });
    }
  }, [timeCard, submitApproveTimeCard, alert, refetch, onClose]);

  const handleChangeDenialReason = (value: string) => {
    setActiveDenialReason(value);
  };

  const handleDenyClick = useCallback(() => {
    const timeCardEntryId =
      timeCard?.timeCardEntries[selectedTimeCardIndex]?.id ?? null;
    if (timeCardEntryId) {
      setShowDenyMode((prev) => !prev);
    }
  }, [selectedTimeCardIndex, timeCard?.timeCardEntries]);

  const handleDenyTimeEntry = useCallback(async () => {
    try {
      const timeCardEntryId =
        timeCard?.timeCardEntries[selectedTimeCardIndex]?.id ?? null;
      const response = await postTimeCardRejectionById({
        id: timeCardEntryId,
        reason: activeDenialReason ?? '',
      });

      try {
        const messageResponse = await submitTimeCardMessage({
          id: timeCardEntryId,
          message: activeDenialReason ?? '',
        });
        if (messageResponse && response) {
          alert({
            message: 'The time card denial was successful.',
            status: 'success',
            timeout: 5000,
          });
        }
      } catch (messageError) {
        console.error(
          'There was an error submitting the time card message',
          messageError,
        );
        alert({
          message:
            'The time card denial was successful, but there was an error submitting the message.',
          status: 'error',
          timeout: 5000,
        });
      }
      setActiveDenialReason('');
      setShowDenyMode(false);
      refetch && refetch();
    } catch (error) {
      console.error('There was an error denying the time card', error);
      alert({
        message: 'There was an error submitting the denial request.',
        status: 'error',
        timeout: 5000,
      });
    }
  }, [
    activeDenialReason,
    alert,
    postTimeCardRejectionById,
    selectedTimeCardIndex,
    submitTimeCardMessage,
    refetch,
    timeCard?.timeCardEntries,
  ]);

  const {
    mutateAsync: deleteSelectedTimeCard,
    isPending: deleteRequestPending,
  } = useMutation({
    mutationFn: (timeCardEntryId: string) =>
      DB.DELETE(`/time-card-entries/${timeCardEntryId}`),
  });

  const handleDeletePress = () => {
    setShowDeleteAlert((prev) => !prev);
  };

  const deleteTimeCard = useCallback(async () => {
    if (selectedTimeCardIndex === null || selectedTimeCardIndex < 0) return;

    const timeCardEntryId =
      timeCard?.timeCardEntries[selectedTimeCardIndex]?.id ?? null;

    if (!timeCardEntryId) return;
    try {
      const { error } = await deleteSelectedTimeCard(timeCardEntryId);
      if (error) {
        throw error;
      }
      alert({
        status: 'success',
        message: 'Time card deleted',
        timeout: 5000,
      });
      refetch && refetch();
      onClose();
    } catch (error) {
      console.error(error);
      alert({
        status: 'error',
        message: 'There was an error deleting this time card entry',
        timeout: 10000,
      });
    }
  }, [
    deleteSelectedTimeCard,
    alert,
    timeCard?.timeCardEntries,
    selectedTimeCardIndex,
    onClose,
    refetch,
  ]);

  // Debug: log timeCard entries when modal renders
  try {
    console.log(
      'EmployeeWorkdaySummaryModal timeCardEntries',
      timeCard?.timeCardEntries?.map((e) => ({
        id: e.id,
        startTime: e.startTime,
        status: e.status,
      })),
    );
  } catch (err) {
    console.warn('Could not log timeCardEntries', err);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent
        borderRadius={0}
        h="$full"
        bg="$gray100"
        maxWidth="90%"
        maxHeight="80%"
      >
        <ModalHeader bg="$primary700" alignItems="center" py="$2">
          <HStack
            h="$full"
            alignItems="center"
            flex={1}
            justifyContent="space-between"
          >
            <HStack alignItems="center" space="md">
              <Text size="xl" color="$white" bold>
                {timeCard?.employee?.fullName}
              </Text>
              <Text size="xl" color="$white">
                -
              </Text>
              <Text size="lg" color="$white">
                {moment(date).format('MMMM DD, YYYY')}
              </Text>
            </HStack>
            <HStack alignItems="center" gap={'$1'}>
              <Text color="$white">
                Today's Date: {moment().tz('America/Chicago').format('M/D/YY')}
              </Text>
              {selectedTimeCardIndex !== null ? (
                <Text color="$white">
                  Last updated:{' '}
                  {moment(
                    timeCard.timeCardEntries[selectedTimeCardIndex].updatedAt,
                  )
                    .utc(true)
                    .tz('America/Chicago')
                    .format('h:mma M/D/YY')}
                </Text>
              ) : (
                // If no individual card is selected, show the most recent updatedAt value of all entries
                <Text color="$white">
                  Last updated:{' '}
                  {timeCard.timeCardEntries.length === 0
                    ? '  -'
                    : moment(
                        timeCard.timeCardEntries.reduce((acc, entry) => {
                          const date = new Date(entry.updatedAt);
                          return date > acc ? date : acc;
                        }, new Date(0)),
                      )
                        .utc(true)
                        .tz('America/Chicago')
                        .format('h:mma M/D/YY')}
                </Text>
              )}
            </HStack>
          </HStack>
          <Button
            w="$12"
            minWidth="auto"
            ml="$6"
            onPress={() => openUnlockModal(timeCard)}
          >
            <Icon color="$white" as={Icons.Lock} />
          </Button>
          <Button ml="$3" w="$12" minWidth="auto" onPress={onClose}>
            <Icon color="$white" size="xl" as={Icons.X} />
          </Button>
        </ModalHeader>
        <HStack flex={1} pt="$8" px="$6" gap="$8">
          <VStack flex={1} h="$full" alignItems="center">
            <Text size="xl" color="$textNormal">
              Total hours: {decimalHoursToTimeString(timeCard.totalTime, false)}
            </Text>
            <EntityCardColumn
              items={timeCard.timeCardEntries}
              CardComponent={({
                data: timeCardEntry,
                index,
              }: {
                data: TimeCardEntry;
                index: number;
              }) => (
                <TimeCardEntrySummaryCard
                  timeCardEntry={timeCardEntry}
                  onPress={() => {
                    setSelectedTimeCardIndex(index);
                    setShowDenyMode(false);
                    setActiveDenialReason('');
                  }}
                  selected={selectedTimeCardIndex === index}
                />
              )}
            />
            <Center p="$4" w="$full">
              <VStack space="lg" w="$full">
                {shouldShowDeleteButton && (
                  <ButtonAsync
                    size="lg"
                    onPress={handleDeletePress}
                    pending={deleteRequestPending}
                    isDisabled={isDisabledStatus}
                  >
                    <ButtonIcon as={Icons.Delete} />
                    <ButtonText>Delete</ButtonText>
                  </ButtonAsync>
                )}
                {timeCard.status !== 'Approved' && (
                  <>
                    <ButtonAsync
                      size="lg"
                      onPress={approveTimeCard}
                      pending={approvalRequestPending}
                    >
                      <ButtonIcon as={Icons.Check} />
                      <ButtonText>Approve</ButtonText>
                    </ButtonAsync>
                    <Button
                      size="lg"
                      onPress={handleDenyClick}
                      bgColor="$primary300"
                      isDisabled={isDisabledStatus}
                    >
                      <ButtonIcon as={Icons.Flag} />
                      <ButtonText>Deny</ButtonText>
                    </Button>
                  </>
                )}
              </VStack>
            </Center>
          </VStack>
          <Divider orientation="vertical" subtle />
          <VStack flex={2}>
            {selectedTimeCardIndex === null ? (
              <Center flex={1}>
                <Text size="lg" color="$textNormal">
                  Select time entry to view
                </Text>
              </Center>
            ) : showDenyMode ? (
              <FormControl size="lg" mt={'$4'}>
                <FormControlLabel>
                  <FormControlLabelText
                    style={{
                      textAlign: 'center',
                      fontWeight: '400',
                      width: '100%',
                      fontFamily: 'Lato',
                      color: '#000',
                    }}
                  >
                    Why are you denying this time entry?
                  </FormControlLabelText>
                </FormControlLabel>
                <Textarea style={{ marginTop: 20 }}>
                  <TextareaInput
                    placeholder="Time denial reason..."
                    onChange={(e) => {
                      e.preventDefault();
                      handleChangeDenialReason(e.nativeEvent.text);
                    }}
                  />
                </Textarea>
                <Button
                  size="lg"
                  onPress={handleDenyTimeEntry}
                  bgColor="$primary700"
                  disabled={!activeDenialReason}
                  mt="$4"
                >
                  <ButtonIcon as={Icons.CheckCircle} />
                  <ButtonText>Send Time Card Denial</ButtonText>
                </Button>
              </FormControl>
            ) : (
              <TimeCardEntryDetails
                key={selectedTimeCardIndex}
                timeCard={timeCard.timeCardEntries[selectedTimeCardIndex]}
                date={date}
                refetch={refetch}
              />
            )}
          </VStack>
        </HStack>
      </ModalContent>
      {showDeleteAlert && (
        <Alert
          action={'warning'}
          position={'absolute'}
          variant="outline"
          flex={1}
          flexDirection="row"
          sx={{
            p: '$4',
            gap: '$2',
            top: 0,
            w: '$full',
            zIndex: 99999,
          }}
        >
          <AlertText>
            {'Are you sure you want to delete the selected time card entry?'}
          </AlertText>

          <ButtonAsync
            size="lg"
            onPress={handleDeletePress}
            pending={deleteRequestPending}
          >
            <ButtonIcon as={Icons.XCircle} />
            <ButtonText>Cancel</ButtonText>
          </ButtonAsync>

          <ButtonAsync
            size="lg"
            onPress={deleteTimeCard}
            pending={deleteRequestPending}
          >
            <ButtonIcon as={Icons.Delete} />
            <ButtonText>Delete</ButtonText>
          </ButtonAsync>
        </Alert>
      )}
    </Modal>
  );
};

export interface TimeCardEntryDetailsProps {
  timeCard: TimeCardEntry;
  date: Date;
  refetch?: () => void;
}

export const TimeCardEntryDetails = ({
  timeCard,
  date,
  refetch,
}: TimeCardEntryDetailsProps) => {
  return (
    <HStack flex={1} gap="$8">
      <ScrollView flex={1} p="$0.5" pb="$10">
        <TimeCardEntryForm
          timeCardEntryId={timeCard?.id}
          targetDate={date}
          onSuccess={refetch}
        />
      </ScrollView>
      <Divider orientation="vertical" subtle />
      <VStack flex={1} pb="$10">
        <MessagesForEntity
          entityKind="time-card-entries"
          entityId={timeCard.id}
        />
      </VStack>
    </HStack>
  );
};
